import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import Tabs from 'components/Tabs';
import Box from '@mui/material/Box';
import { styled } from '@mui/system';

import { LOCATION_DETAIL } from 'bento-ordering/locations/constants/entities';
import { MENU } from 'bento-ordering/menus/constants/entities';
import { LOCATION_BASE } from 'bento-ordering/locations/constants/paths';
import {
  ALL_ONLINE_ORDERING_FULFILLMENT_METHODS,
  fulfillmentOptions
} from 'constants/fulfillmentOptions';
import { PREVIEW_MENU } from 'constants/previewMenu';
import { isMobileMedia } from 'bento-ordering/utils/media';
import {
  selectFulfillmentDateOptionsForLocationAndMethod,
  getAvailableFulfillmentMethodSelector,
  isLocationClosedSelector,
  getIsPreviewMenuOn
} from 'bento-ordering/locations/selectors';

import withLoading from 'bento-ordering/base/hoc/withLoading';

import LocationDateTime from 'bento-ordering/locations/components/LocationDateTime';
import OrderSettingsDetailHeader from 'components/OrderSettingsDetailHeader';
import ModalCloseButton from 'bento-ordering/base/ui_components/ModalCloseButton';
import AddressAutoCompleteInput from 'components/AddressAutoCompleteInput';
import AddressInput from 'components/AddressInput';
import {
  NOT_DELIVERABLE,
  NOT_DELIVERABLE_NO_PICKUP
} from 'constants/errorTypes';
import { toTitleCase } from 'bento-ordering/base/utils/textUtils';

import { getNextDay } from 'bento-ordering/base/utils/timeHelpers';

import { handleAddressSelect } from 'bento-ordering/locations/actions/locations';
import {
  isDinerAddressDeliverableSelector,
  areFulfillmentOptionsValidSelector,
  getAsapLaterOptionsSelector
} from 'bento-ordering/cart/reducers/cartWorkingCopy';

import {
  setWorkingCopyFromCart,
  resetCartWorkingCopy,
  setCartWorkingCopy,
  resetCartWorkingCopyDeliveryAddress
} from 'bento-ordering/cart/actions/cartWorkingCopy';

import setCartFulfillmentOptions from 'bento-ordering/locations/actions/setCartFulfillmentOptions';
import verifyCartItemsStillFulfillable from 'bento-ordering/cart/actions/verifyCartItemsStillFulfillable';
import {
  processHoursList,
  getFirstEnabled,
  getFirstAvailableDateTime,
  findDateOption,
  getDoesDateHaveTimeEnabled
} from 'bento-ordering/locations/utils/fulfillmentOptionsFilter';
import { OVERNIGHT_HOURS } from 'bento-ordering/locations/constants';
import PageLoadSpinner from 'components/PageLoadSpinner';
import { resetScrollPosition } from 'bento-ordering/base/actions/scrollManager';
import { getAddressStringFromCart } from 'helpers/addressHelpers';
import { getPageBackgroundColor } from 'styles/helpers/getThemeStyles';

const StyledBox = styled(Box)(({ theme }) => {
  return {
    backgroundColor: getPageBackgroundColor(theme)
  };
});

const getPrimaryCtaLabel = (
  { online_ordering_fulfillment_method, requested_fulfillment_is_asap },
  isDinerAddressDeliverable,
  isPreviewMenuOn = false
) => {
  const verb = requested_fulfillment_is_asap ? 'Continue' : 'Schedule';

  if (
    online_ordering_fulfillment_method === fulfillmentOptions.DELIVERY &&
    !isDinerAddressDeliverable
  ) {
    return 'Enter Address to Start';
  }

  if (isPreviewMenuOn) {
    return PREVIEW_MENU;
  }

  return `${verb} as ${online_ordering_fulfillment_method}`;
};

class SelectedLocationContainer extends React.Component {
  static propTypes = {
    closeModal: PropTypes.func.isRequired,
    areCartItemsAvailable: PropTypes.bool,
    dispatch: PropTypes.func,
    fulfillmentDateOptions: PropTypes.arrayOf(PropTypes.object),
    cartWorkingCopy: PropTypes.object,
    locationIsClosed: PropTypes.bool,
    availableFulfillmentOptions: PropTypes.arrayOf(
      PropTypes.oneOf(ALL_ONLINE_ORDERING_FULFILLMENT_METHODS)
    ),
    isDinerAddressDeliverable: PropTypes.bool,
    areFulfillmentOptionsValid: PropTypes.bool,
    asapOrLaterOptions: PropTypes.arrayOf(PropTypes.shape({})),
    isAsapAvailableForDelivery: PropTypes.bool,
    isAsapAvailableForPickup: PropTypes.bool,
    googleLoaded: PropTypes.bool,
    addressErrorExist: PropTypes.bool,
    isPreviewMenuOn: PropTypes.bool.isRequired
  };

  static defaultProps = {
    fulfillmentOptions: {}
  };

  async componentDidMount() {
    this.setScrollPosition();
    await this.setWorkingCopyFromCart();
    // fulfillment options made not be available
    // so we do an extra check here
    this.updateWorkingCopyFulfillmentOption();
  }

  componentDidUpdate(prevProps) {
    this.updateWorkingCopyAsapLaterOnUpdate(prevProps);
    this.updateWorkingCopyFulfillmentDateTime(prevProps);
    this.verifyCartItemsStillFulfillable(prevProps);
  }

  /**
   * Set menu scroll position back to 0 because it's a new flow
   */
  setScrollPosition = () => {
    const selectedFulfillmentMethod = sessionStorage.getItem(
      'selectedFulfillmentMethod'
    );
    if (isMobileMedia() && !selectedFulfillmentMethod) {
      this.props.dispatch(resetScrollPosition(MENU));
    }
  };

  setWorkingCopyFromCart() {
    const { dispatch, addressErrorExist } = this.props;

    dispatch(setWorkingCopyFromCart());

    // force the user to enter a address for validation of an address error occured
    if (addressErrorExist) {
      dispatch(resetCartWorkingCopyDeliveryAddress());
    }
  }

  componentWillUnmount() {
    this.cleanup();
  }

  cleanup() {
    this.props.dispatch(resetCartWorkingCopy());
  }

  /**
   * calls updateWorkingCopyFulfillmentOption if
   * the availability of asap for either pickup or delivery
   * changes, for instance, when the payload from
   * location throttled times has loaded
   * @memberof SelectedLocationContainer
   * @param {{}} prevProps passed in from componentDidUpdate
   * @param {boolean} prevProps.isAsapAvailableForPickup
   * @param {boolean} prevProps.isAsapAvailableForDelivery
   * @return {void}
   */
  updateWorkingCopyAsapLaterOnUpdate(prevProps) {
    if (
      prevProps.isAsapAvailableForPickup !==
        this.props.isAsapAvailableForPickup ||
      prevProps.isAsapAvailableForDelivery !==
        this.props.isAsapAvailableForDelivery
    ) {
      this.updateWorkingCopyFulfillmentOption();
    }
  }

  /**
   * This method updates the fulfillment method to either ASAP or
   * later when the order setting modal is rendered. The previous set
   * fulfillment method may not be available so we have to do an extra
   * here. The fulfillment method is initialized to where asap is available
   * for delivery and updated to be base on pickup if pickup is available is
   * fulfillment option.
   */
  updateWorkingCopyFulfillmentOption = () => {
    const {
      cartWorkingCopy,
      isAsapAvailableForDelivery,
      isAsapAvailableForPickup
    } = this.props;

    let requestedFulfillmentIsAsap = isAsapAvailableForDelivery;

    if (
      cartWorkingCopy.online_ordering_fulfillment_method ===
      fulfillmentOptions.PICKUP
    ) {
      requestedFulfillmentIsAsap = isAsapAvailableForPickup;
    }
    const payload = {
      requested_fulfillment_is_asap: requestedFulfillmentIsAsap
    };
    this.props.dispatch(setCartWorkingCopy(payload));
  };

  shouldSetFirstAvailableDateTime(
    prevProps,
    firstAvailableDate,
    firstAvailableTime
  ) {
    const { cartWorkingCopy } = this.props;

    if (cartWorkingCopy.requested_fulfillment_is_asap) {
      return false;
    }

    const prevAsapValue =
      prevProps.cartWorkingCopy.requested_fulfillment_is_asap;

    const prevAsapValueWasSetToAsap = prevAsapValue === true;
    const prevAsapValueWasNotSet = prevAsapValue === null;

    const shouldSetForAsapChange =
      prevAsapValueWasSetToAsap || prevAsapValueWasNotSet;
    const [
      prevFirstAvailableDate,
      prevFirstAvailableTime
    ] = getFirstAvailableDateTime(prevProps.fulfillmentDateOptions);

    const firstAvailDateDidChange =
      firstAvailableDate.value !== prevFirstAvailableDate.value;

    const firstAvailTimeDidChange =
      firstAvailableTime.time !== prevFirstAvailableTime.time;

    return (
      shouldSetForAsapChange ||
      firstAvailDateDidChange ||
      firstAvailTimeDidChange
    );
  }

  shouldUpdateTime(prevProps, currentDateOption, firstAvailableTimeForDate) {
    const { cartWorkingCopy } = this.props;
    const didDateChange =
      cartWorkingCopy.fulfillment_date !==
      prevProps.cartWorkingCopy.fulfillment_date;

    const dateHasCurrentlySelectedOption = getDoesDateHaveTimeEnabled(
      currentDateOption,
      cartWorkingCopy.fulfillment_time
    );

    return (
      didDateChange &&
      !dateHasCurrentlySelectedOption &&
      firstAvailableTimeForDate
    );
  }

  /**
   * This method updates the cart working copy fulfillment date
   * if the fulfillment option changed from asap to no asap, or if
   * the first date or time becomes disabled because of the loading
   * of location throttled times
   *
   * @param {Object} prevProps
   * @return {void}
   */
  updateWorkingCopyFulfillmentDateTime = prevProps => {
    const { fulfillmentDateOptions, cartWorkingCopy } = this.props;

    const [firstAvailableDate, firstAvailableTime] = getFirstAvailableDateTime(
      fulfillmentDateOptions
    );

    const shouldSetFirstAvailableDateTime = this.shouldSetFirstAvailableDateTime(
      prevProps,
      firstAvailableDate,
      firstAvailableTime
    );

    const currentDateOption = findDateOption(
      fulfillmentDateOptions,
      cartWorkingCopy.fulfillment_date
    );
    const firstAvailableTimeForDate = getFirstEnabled(
      currentDateOption.timeOptions
    );
    const shouldUpdateTime = this.shouldUpdateTime(
      prevProps,
      currentDateOption,
      firstAvailableTimeForDate
    );

    let fulfillmentPayload = null;
    if (shouldSetFirstAvailableDateTime) {
      fulfillmentPayload = {
        fulfillment_date: firstAvailableDate.value,
        fulfillment_time: firstAvailableTime.time
      };
    } else if (shouldUpdateTime) {
      fulfillmentPayload = {
        fulfillment_time: firstAvailableTimeForDate.time
      };
    }

    if (fulfillmentPayload) {
      this.props.dispatch(setCartWorkingCopy(fulfillmentPayload));
    }
  };

  /**
   * Update fulfillment method and requested is asap. If asap is not
   * available for the new fulfillment method
   */
  updateWorkingCopyFulfillmentMethod = method => {
    const { isAsapAvailableForDelivery, isAsapAvailableForPickup } = this.props;

    let requestedFulfillmentIsAsap = isAsapAvailableForDelivery;
    if (method === fulfillmentOptions.PICKUP) {
      requestedFulfillmentIsAsap = isAsapAvailableForPickup;
    }
    const payload = {
      online_ordering_fulfillment_method: method,
      requested_fulfillment_is_asap: requestedFulfillmentIsAsap
    };

    this.props.dispatch(setCartWorkingCopy(payload));
  };

  updateWorkingCopySecondaryAddress = secondaryAddress => {
    const payload = { shipping_street_address_2: secondaryAddress };

    this.props.dispatch(setCartWorkingCopy(payload));
  };

  handleAddressSelect = (address, latLng, placeId) => {
    this.props.dispatch(
      handleAddressSelect(address, placeId, latLng.lat, latLng.lng)
    );
  };

  verifyCartItemsStillFulfillable(prevProps) {
    const { cartWorkingCopy } = this.props;
    if (
      cartWorkingCopy.fulfillment_date !==
        prevProps.cartWorkingCopy.fulfillment_date ||
      cartWorkingCopy.fulfillment_time !==
        prevProps.cartWorkingCopy.fulfillment_time ||
      cartWorkingCopy.requested_fulfillment_is_asap !==
        prevProps.cartWorkingCopy.requested_fulfillment_is_asap ||
      cartWorkingCopy.online_ordering_fulfillment_method !==
        prevProps.cartWorkingCopy.online_ordering_fulfillment_method
    ) {
      this.props.dispatch(verifyCartItemsStillFulfillable());
    }
  }

  handleAsapLaterSelectChange = e => {
    const { dispatch, fulfillmentDateOptions } = this.props;
    const option = e.target.value;
    const requestedAsap = Number(option) === 1;
    dispatch(
      setCartWorkingCopy({
        requested_fulfillment_is_asap: requestedAsap,
        fulfillment_date: !requestedAsap
          ? fulfillmentDateOptions[0].value
          : undefined
      })
    );
  };

  goToMenu = e => {
    e.preventDefault();
    this.props.dispatch(setCartFulfillmentOptions());
  };

  handleDateChange = date => {
    const { dispatch } = this.props;
    dispatch(
      setCartWorkingCopy({
        fulfillment_date: date
      })
    );
  };

  /**
   * Updates the cart working copy to have new selected time. If the
   * user selects midnight or an overnight hour, update the
   * workingCopy fulfillment date to be the next day as well
   */
  handleTimeChange = e => {
    const { dispatch, cartWorkingCopy } = this.props;
    const timeOptionElements = e.target.options;
    const selectedIndex = e.target.selectedIndex;
    const isOvernight =
      timeOptionElements[selectedIndex].parentNode.label === OVERNIGHT_HOURS;

    const payload = {
      fulfillment_time: e.target.value
    };

    if (isOvernight) {
      const currentlySelectedDate = cartWorkingCopy.fulfillment_date;
      const nextDay = getNextDay(currentlySelectedDate);
      payload.fulfillment_date = nextDay;
    }

    dispatch(setCartWorkingCopy(payload));
  };

  getNotDeliverableError() {
    const {
      cartWorkingCopy,
      availableFulfillmentOptions,
      isDinerAddressDeliverable
    } = this.props;

    const isNotValidatingAddress = !cartWorkingCopy.isValidatingAddress;
    const hasValidationError = cartWorkingCopy.error.length;
    const noPickupOption =
      availableFulfillmentOptions.length === 1 &&
      availableFulfillmentOptions.includes(fulfillmentOptions.DELIVERY);

    if (
      isNotValidatingAddress &&
      noPickupOption &&
      (hasValidationError || isDinerAddressDeliverable === false)
    ) {
      return NOT_DELIVERABLE_NO_PICKUP;
    } else if (
      isNotValidatingAddress &&
      (hasValidationError || isDinerAddressDeliverable === false)
    ) {
      return NOT_DELIVERABLE;
    } else {
      return '';
    }
  }

  render() {
    const {
      closeModal,
      areCartItemsAvailable,
      cartWorkingCopy,
      locationIsClosed,
      fulfillmentDateOptions,
      availableFulfillmentOptions,
      isDinerAddressDeliverable,
      areFulfillmentOptionsValid,
      asapOrLaterOptions,
      googleLoaded,
      isPreviewMenuOn
    } = this.props;

    /**
     * Redirect to locations list if the specified location
     * is offline
     */
    if (locationIsClosed || !availableFulfillmentOptions.length) {
      if (!isMobileMedia()) {
        closeModal();
      }
      return <Redirect to={LOCATION_BASE} />;
    }

    if (!googleLoaded) {
      return <PageLoadSpinner />;
    }

    const getActiveTab = (
      onlineOrderingFulfillmentMethod,
      availableFulfillmentOptions
    ) => {
      const methodToValueLookup = ALL_ONLINE_ORDERING_FULFILLMENT_METHODS;
      if (
        availableFulfillmentOptions.length === 2 &&
        onlineOrderingFulfillmentMethod
      ) {
        return methodToValueLookup.indexOf(onlineOrderingFulfillmentMethod);
      }
      return 0;
    };

    const activeTab = getActiveTab(
      cartWorkingCopy.online_ordering_fulfillment_method,
      availableFulfillmentOptions
    );
    const initialAddressValue = getAddressStringFromCart(cartWorkingCopy);
    const timeOptionsForDate = findDateOption(
      fulfillmentDateOptions,
      cartWorkingCopy.fulfillment_date
    ).timeOptions;
    const processedHoursList = processHoursList(timeOptionsForDate);

    return (
      <StyledBox>
        <ModalCloseButton autoFocus handleClick={closeModal} />
        <Box pl={2.5} pr={2.5}>
          <Tabs
            activeTab={activeTab}
            handleTabClick={this.updateWorkingCopyFulfillmentMethod}
            tabLabels={availableFulfillmentOptions}
          />
        </Box>
        <Box p={2.5}>
          <Box mb={2}>
            <OrderSettingsDetailHeader
              title={`Order for ${toTitleCase(
                cartWorkingCopy.online_ordering_fulfillment_method
              )}`}
            />
          </Box>

          {cartWorkingCopy.online_ordering_fulfillment_method ===
            fulfillmentOptions.DELIVERY && (
            <>
              <AddressAutoCompleteInput
                handleSelect={this.handleAddressSelect}
                error={this.getNotDeliverableError()}
                initialValue={initialAddressValue}
                includeAdornment
              />
              <Box mt={2.5}>
                <AddressInput
                  onChange={e =>
                    this.updateWorkingCopySecondaryAddress(e.target.value)
                  }
                  value={cartWorkingCopy.shipping_street_address_2 || ''}
                  type="text"
                  ariaAutocomplete="inline"
                  label="Apt/Floor/Suite (optional)"
                />
              </Box>
            </>
          )}
          {cartWorkingCopy.online_ordering_fulfillment_method && (
            <LocationDateTime
              primaryButtonLabel={getPrimaryCtaLabel(
                cartWorkingCopy,
                isDinerAddressDeliverable,
                isPreviewMenuOn
              )}
              fulfillmentIsAsap={cartWorkingCopy.requested_fulfillment_is_asap}
              selectedTime={cartWorkingCopy.fulfillment_time}
              selectedDate={cartWorkingCopy.fulfillment_date}
              areCartItemsAvailable={areCartItemsAvailable}
              fulfillmentAsapLaterOptions={asapOrLaterOptions}
              fulfillmentDateOptions={fulfillmentDateOptions}
              fulfillmentHourOptions={processedHoursList}
              areFulfillmentOptionsValid={areFulfillmentOptionsValid}
              fulfillmentMethod={
                cartWorkingCopy.online_ordering_fulfillment_method
              }
              isValidatingAddress={cartWorkingCopy.isValidatingAddress}
              handleTimeChange={this.handleTimeChange}
              handleDateChange={this.handleDateChange}
              handleAsapLaterSelectChange={this.handleAsapLaterSelectChange}
              handleSubmit={this.goToMenu}
            />
          )}
        </Box>
      </StyledBox>
    );
  }
}

const mapStateToProps = state => {
  const {
    restaurantLocations,
    locationThrottledTimes,
    cartAvailable,
    cartWorkingCopy,
    cart,
    libraryLoad,
    cartValidation
  } = state;

  const isClosed = isLocationClosedSelector(
    restaurantLocations,
    cart.online_ordering_location
  );

  const areCartItemsAvailable = cartAvailable.isAvailable;
  const fulfillmentDateOptions = selectFulfillmentDateOptionsForLocationAndMethod(
    {
      restaurantLocations,
      cart: cartWorkingCopy.online_ordering_fulfillment_method
        ? cartWorkingCopy
        : cart,
      locationThrottledTimes
    }
  );

  const availableFulfillmentOptions = getAvailableFulfillmentMethodSelector({
    restaurantLocations,
    locationThrottledTimes,
    cart,
    onlineOrderingLocation: cart.online_ordering_location
  });
  const areFulfillmentOptionsValid = areFulfillmentOptionsValidSelector({
    cartWorkingCopyState: cartWorkingCopy
  });
  const asapOrLaterOptions = getAsapLaterOptionsSelector(
    restaurantLocations,
    locationThrottledTimes,
    cartWorkingCopy
  );
  const googleLoaded = libraryLoad.google;
  const addressErrorExist = cartValidation.addressError;

  const isPreviewMenuOn = getIsPreviewMenuOn(state);

  return {
    areCartItemsAvailable,
    cartWorkingCopy,
    fulfillmentDateOptions,
    locationIsClosed: isClosed,
    availableFulfillmentOptions,
    isDinerAddressDeliverable: isDinerAddressDeliverableSelector(
      cartWorkingCopy
    ),
    areFulfillmentOptionsValid,
    asapOrLaterOptions,
    googleLoaded,
    addressErrorExist,
    isPreviewMenuOn
  };
};

export default connect(mapStateToProps)(
  withLoading(SelectedLocationContainer, LOCATION_DETAIL)
);
